// src/admin/components/WarehouseView.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  CircularProgress,
  IconButton,
  Button,
  Collapse,
  Stack,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Warehouse as WarehouseIcon,
  Inventory as InventoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  TransferWithinAStation as TransferIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { Warehouse, WarehouseWithInventory, WarehouseInventory } from '../../../enums/product';
import { WarehouseService } from '../warehouse/warehouseService';
import { WarehouseInventoryService } from '../../pages/product/WarehouseInventoryService';
import TransferDialog from './TransferDialog';

interface WarehouseViewProps {
  warehouseId: string;
  onClose: () => void;
  onEdit: (warehouse: Warehouse) => void;
   canTransfer?: boolean;
}

interface GroupedProduct {
  productId: string;
  productName: string;
  sku: string;
  hasVariants: boolean;
  totalQuantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isOutOfStock: boolean;
  isLowStock: boolean;
  isOverStock: boolean;
  variants: WarehouseInventory[];
}

// ── Theme-aware chip style helper ─────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.12)',  border: 'rgba(255,167,38,0.4)',   text: '#b76e00', iconColor: '#e68900' },
    info:      { bg: 'rgba(41,182,246,0.12)',   border: 'rgba(41,182,246,0.4)',   text: '#0277bd', iconColor: '#039be5' },
    primary:   { bg: 'rgba(33,150,243,0.12)',   border: 'rgba(33,150,243,0.4)',   text: '#1565c0', iconColor: '#1976d2' },
    success:   { bg: 'rgba(102,187,106,0.15)',  border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error:     { bg: 'rgba(239,83,80,0.12)',    border: 'rgba(239,83,80,0.4)',    text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)',   border: 'rgba(171,71,188,0.35)',  text: '#6a1b9a', iconColor: '#8e24aa' },
    default:   { bg: 'rgba(158,158,158,0.12)',  border: 'rgba(158,158,158,0.4)',  text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.22)',  border: 'rgba(255,167,38,0.55)',  text: '#ffcc80', iconColor: '#ffa726' },
    info:      { bg: 'rgba(41,182,246,0.20)',   border: 'rgba(41,182,246,0.50)',  text: '#81d4fa', iconColor: '#29b6f6' },
    primary:   { bg: 'rgba(33,150,243,0.22)',   border: 'rgba(33,150,243,0.50)',  text: '#90caf9', iconColor: '#42a5f5' },
    success:   { bg: 'rgba(102,187,106,0.22)',  border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error:     { bg: 'rgba(239,83,80,0.22)',    border: 'rgba(239,83,80,0.50)',   text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)',   border: 'rgba(171,71,188,0.50)',  text: '#ce93d8', iconColor: '#ab47bc' },
    default:   { bg: 'rgba(158,158,158,0.22)',  border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

const stockStatusColor = (isOutOfStock: boolean, isLowStock: boolean, isOverStock: boolean): string => {
  if (isOutOfStock) return 'error';
  if (isLowStock)   return 'warning';
  if (isOverStock)  return 'warning';
  return 'success';
};

const stockStatusLabel = (isOutOfStock: boolean, isLowStock: boolean, isOverStock: boolean): string => {
  if (isOutOfStock) return 'Out of Stock';
  if (isLowStock)   return 'Low Stock';
  if (isOverStock)  return 'Over Stock';
  return 'In Stock';
};
// ─────────────────────────────────────────────────────────────────────────────

const WarehouseView: React.FC<WarehouseViewProps> = ({
  warehouseId,
  onClose,
  onEdit,
  canTransfer = false, 
}) => {
  const [warehouse, setWarehouse] = useState<WarehouseWithInventory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    loadWarehouseData();
  }, [warehouseId]);

  const loadWarehouseData = async () => {
    try {
      setLoading(true);
      const data = await WarehouseService.getWarehouseWithInventory(warehouseId);
      setWarehouse(data);
    } catch (err: any) {
      console.error('Error loading warehouse data:', err);
      setError('Failed to load warehouse data');
    } finally {
      setLoading(false);
    }
  };

  const handleTransferStock = (product: any) => {
    setSelectedProduct(product);
    setTransferDialogOpen(true);
  };

  const handleTransferSubmit = async (toWarehouseId: string, quantity: number) => {
    try {
      await WarehouseInventoryService.transferStock(
        warehouseId,
        toWarehouseId,
        selectedProduct.productId,
        quantity,
        selectedProduct.variantId
      );
      setTransferDialogOpen(false);
      setSelectedProduct(null);
      await loadWarehouseData();
    } catch (err: any) {
      setError(err.message || 'Failed to transfer stock');
    }
  };

  const toggleProductExpansion = (productId: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const getGroupedProducts = (): GroupedProduct[] => {
    if (!warehouse?.inventory) return [];

    const grouped = new Map<string, GroupedProduct>();

    warehouse.inventory.forEach(item => {
      if (!grouped.has(item.productId)) {
        grouped.set(item.productId, {
          productId: item.productId,
          productName: item.productName || '',
          sku: item.sku || '',
          hasVariants: !!item.variantId,
          totalQuantity: 0,
          minStockLevel: item.minStockLevel,
          maxStockLevel: item.maxStockLevel,
          isOutOfStock: false,
          isLowStock: false,
          isOverStock: false,
          variants: [],
        });
      }

      const group = grouped.get(item.productId)!;

      if (item.variantId) {
        group.variants.push(item);
        group.totalQuantity += item.quantity;
      } else {
        group.totalQuantity = item.quantity;
        group.isOutOfStock = item.isOutOfStock;
        group.isLowStock = item.isLowStock;
        group.isOverStock = item.isOverStock;
      }
    });

    grouped.forEach(group => {
      if (group.hasVariants && group.variants.length > 0) {
        group.isOutOfStock = group.variants.every(v => v.isOutOfStock);
        group.isLowStock = !group.isOutOfStock && group.variants.some(v => v.isLowStock);
        group.isOverStock = group.variants.some(v => v.isOverStock);
      }
    });

    return Array.from(grouped.values());
  };

  const calculateStats = () => {
    if (!warehouse?.inventory) {
      return { productCount: 0, totalStock: 0, lowStockCount: 0, inStockCount: 0 };
    }

    const groupedProducts = getGroupedProducts();
    const totalStock = warehouse.inventory.reduce((sum, item) => sum + item.quantity, 0);
    const lowStockCount = groupedProducts.filter(p => p.isLowStock && !p.isOutOfStock).length;
    const inStockCount = groupedProducts.filter(p => !p.isOutOfStock && !p.isLowStock).length;

    return { productCount: groupedProducts.length, totalStock, lowStockCount, inStockCount };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !warehouse) {
    return (
      <Alert severity="error" onClose={onClose}>
        {error || 'Warehouse not found'}
      </Alert>
    );
  }

  const utilizationRate = Math.round(
    (warehouse.capacity.used / warehouse.capacity.total) * 100
  );

  const stats = calculateStats();
  const groupedProducts = getGroupedProducts();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{
        p: 3,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarehouseIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" gutterBottom>
                {warehouse.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {warehouse.code} • {warehouse.address.city}, {warehouse.address.country}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            {/* <Button startIcon={<EditIcon />} variant="outlined" onClick={() => onEdit(warehouse)} size="small">Edit</Button> */}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 1.5, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Capacity Utilization
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={utilizationRate}
                  sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                  color={utilizationRate > 90 ? 'error' : utilizationRate > 70 ? 'warning' : 'success'}
                />
                <Typography variant="subtitle1" fontWeight={600}>
                  {utilizationRate}%
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {warehouse.capacity.used.toLocaleString()} / {warehouse.capacity.total.toLocaleString()} units used
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 1.5, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Total Products
              </Typography>
              <Typography variant="h5" color="primary" sx={{ mb: 0.5 }}>
                {stats.productCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Unique products in warehouse
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 1.5, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Total Stock
              </Typography>
              <Typography variant="h5" color="primary" sx={{ mb: 0.5 }}>
                {stats.totalStock.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total units in warehouse
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 1.5, height: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Stock Status
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${stats.inStockCount} In Stock`}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx('success', isDark)}
                />
                <Chip
                  icon={<WarningIcon />}
                  label={`${stats.lowStockCount} Low Stock`}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx('warning', isDark)}
                />
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Inventory Table */}
        <Card>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Inventory ({groupedProducts.length} Products)
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {/* Expand toggle column */}
                  <TableCell sx={{ width: 48, p: 1 }} />
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align="right">Total Stock</TableCell>
                  <TableCell align="right">Min</TableCell>
                  <TableCell align="right">Max</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedProducts.map((product) => (
                  <React.Fragment key={product.productId}>
                    {/* ── Parent product row ── */}
                    <TableRow
                      hover
                      sx={{
                        backgroundColor: product.hasVariants ? 'action.hover' : 'inherit',
                        // Remove bottom border when expanded so variants feel attached
                        ...(product.hasVariants && expandedProducts.has(product.productId) && {
                          '& > td': { borderBottom: 'none' },
                        }),
                      }}
                    >
                      {/* Expand button */}
                      <TableCell sx={{ width: 48, p: 1 }}>
                        {product.hasVariants && (
                          <IconButton
                            size="small"
                            onClick={() => toggleProductExpansion(product.productId)}
                          >
                            {expandedProducts.has(product.productId)
                              ? <ExpandLessIcon />
                              : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {product.productName}
                        </Typography>
                        {product.hasVariants && (
                          <Typography variant="caption" color="text.secondary">
                            {product.variants.length} variants
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>{product.sku}</TableCell>

                      <TableCell>
                        <Chip
                          label={product.hasVariants ? 'Variable' : 'Simple'}
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx(product.hasVariants ? 'secondary' : 'default', isDark)}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          {product.totalQuantity.toLocaleString()}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">{product.minStockLevel}</TableCell>
                      <TableCell align="right">{product.maxStockLevel}</TableCell>

                      <TableCell>
                        <Chip
                          label={stockStatusLabel(product.isOutOfStock, product.isLowStock, product.isOverStock)}
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx(stockStatusColor(product.isOutOfStock, product.isLowStock, product.isOverStock), isDark)}
                        />
                      </TableCell>

                      <TableCell>
                        {!product.hasVariants && canTransfer && (
                          <IconButton
                            size="small"
                            onClick={() => handleTransferStock({
                              productId: product.productId,
                              productName: product.productName,
                              sku: product.sku,
                              quantity: product.totalQuantity,
                              variantId: null,
                            })}
                            title="Transfer Stock"
                          >
                            <TransferIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* ── Variant rows (rendered directly in the same <TableBody>
                         so they share column widths with the parent table) ── */}
                    {product.hasVariants && product.variants.map((variant) => (
                      <TableRow
                        key={variant.id}
                        sx={{
                          backgroundColor: 'action.selected',
                          display: expandedProducts.has(product.productId) ? 'table-row' : 'none',
                        }}
                      >
                        {/* Empty expand-toggle cell */}
                        <TableCell sx={{ width: 48, p: 1 }} />

                        {/* Product / variant name — indented */}
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                            ↳ {variant.variantName}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {variant.sku}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label="Variant"
                            size="small"
                            variant="outlined"
                            sx={getSoftChipSx('secondary', isDark)}
                          />
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2">
                            {variant.quantity.toLocaleString()}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {variant.minStockLevel}
                          </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2" color="text.secondary">
                            {variant.maxStockLevel}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={stockStatusLabel(variant.isOutOfStock, variant.isLowStock, variant.isOverStock)}
                            size="small"
                            variant="outlined"
                            sx={getSoftChipSx(stockStatusColor(variant.isOutOfStock, variant.isLowStock, variant.isOverStock), isDark)}
                          />
                        </TableCell>

                        <TableCell>
                          {canTransfer && (
                          <IconButton
                            size="small"
                            onClick={() => handleTransferStock(variant)}
                            title="Transfer Stock"
                          >
                            <TransferIcon fontSize="small" />
                          </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}

                {groupedProducts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No inventory items found in this warehouse
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      {/* Transfer Dialog */}
      <TransferDialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        onSubmit={handleTransferSubmit}
        product={selectedProduct}
        sourceWarehouse={warehouse}
      />
    </Box>
  );
};

export default WarehouseView;